import { ElementType } from 'react';
import { sprintf } from 'sprintf-js';

import { ApiFilestackImageData } from 'api/types/ApiFilestackImageData';
import { FieldError } from 'components/Form/Field/FieldError';
import { Icon } from 'components/Icon';
import { InfoBox } from 'components/InfoBox';
import { Spinner } from 'components/Spinner';
import { BodySmall } from 'components/Text/BodySmall';
import { FilestackPicker } from 'containers/FilestackPicker';
import { colors, transition } from 'theme/theme';
import { isDefined } from 'utils/functional';
import { getRgbaString } from 'utils/style';

import { DropContainer } from './DropContainer';
import { ImagePickerButton } from './ImagePickerButton';
import { ImagePickerOverlayButton } from './ImagePickerOverlayButton';
import { ImagePickerPreview } from './ImagePickerPreview';
import { TrashDeleteButton } from './TrashDeleteButton';

type Props = {
  image: ApiFilestackImageData | null | undefined;
  filestackPickerId?: string;
  name: string;
  onChange: (newImage: ApiFilestackImageData | null | undefined) => void;
  format?: 'logo' | 'banner' | 'avatar';
  message?: string;
  centerButton?: boolean;
  overlayButton?: boolean;
  buttonOnly?: boolean;
  aspectRatio?: number;
  hideRemove?: boolean;
  id?: string;
  'data-qa-id': string;
  showConfirmationModal?: () => void;
  disableCrop?: boolean;
  recommendedDimensions?: { width: number; height: number };
  label?: string;
  extraAnalyticsData?: Record<string, unknown>;
  insecureSVG?: boolean;
  // Preview props
  altText?: string;
  previewCrop?: boolean;
  previewCropToCircle?: boolean;
  previewWidth?: number;
  previewHeight?: number;
  defaultImage?: ElementType;
  saving?: boolean;
};

export function ImagePicker({
  id,
  image,
  filestackPickerId,
  name,
  onChange,
  previewWidth,
  previewHeight,
  format,
  message = getText('Choose a file or drag it here.'),
  centerButton,
  overlayButton,
  buttonOnly,
  aspectRatio,
  hideRemove,
  'data-qa-id': qaId,
  showConfirmationModal,
  disableCrop,
  recommendedDimensions,
  label,
  extraAnalyticsData,
  insecureSVG,
  // Preview props
  altText,
  previewCrop,
  previewCropToCircle,
  defaultImage,
  saving,
}: Props) {
  const isBannerWithOverlay = format === 'banner' && overlayButton;

  const onChangeHandler = (file: ApiFilestackImageData | null | undefined) => {
    onChange(file);
  };

  const showRecommendedDimensionsSuggestion =
    recommendedDimensions &&
    image &&
    ((image.width && recommendedDimensions.width > image.width) ||
      (image.height && recommendedDimensions.height > image.height));

  return (
    <FilestackPicker
      data-qa-id={`${qaId}-select-image`}
      data-qa-has-image={Boolean(image)}
      id={filestackPickerId}
      name={name}
      aspectRatio={aspectRatio}
      disableCrop={disableCrop}
      onChange={onChangeHandler}
      getDimensions={Boolean(recommendedDimensions)}
      label={label}
      extraAnalyticsData={extraAnalyticsData}
      insecureSVG={insecureSVG}
    >
      {({ dragActive, onClick, error, loading }) => {
        if (saving || loading) {
          return (
            <div
              style={{
                height: 100,
                width: previewWidth || 96,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div style={{ width: 32 }}>
                <Spinner size="small" />
              </div>
            </div>
          );
        }

        return (
          <>
            {showRecommendedDimensionsSuggestion && (
              <div style={{ marginBottom: 20 }}>
                <InfoBox $type="warning">
                  <BodySmall>
                    {sprintf(
                      getText(
                        'Your image looks a bit small. Choose an image %(width)s x %(height)s or larger for best results.',
                      ),
                      {
                        width: recommendedDimensions.width,
                        height: recommendedDimensions.height,
                      },
                    )}
                  </BodySmall>
                </InfoBox>
              </div>
            )}

            <div style={{ position: 'relative' }}>
              {!isBannerWithOverlay && (
                <div
                  style={{
                    position: 'absolute',
                    left: -12,
                    right: -12,
                    top: buttonOnly ? 0 : -12,
                    bottom: buttonOnly ? 0 : -12,
                    borderRadius: 4,
                    background: `rgba(${getRgbaString(colors.skyBlue, 0.2)})`,
                    opacity: dragActive ? 1 : 0,
                    transition: `opacity ${transition}`,
                  }}
                />
              )}

              <DropContainer
                position="relative"
                display="inline-flex"
                flexWrap="wrap"
                alignItems="center"
                flexDirection={centerButton ? 'column' : undefined}
                width={format === 'banner' || centerButton ? '100%' : undefined}
                $dragActive={dragActive}
                $hasImage={isDefined(image)}
                $overlayButton={overlayButton}
              >
                {!buttonOnly && (
                  <div
                    style={{
                      width: format === 'banner' ? '100%' : undefined,
                      position: 'relative',
                      marginRight: format !== 'banner' ? 48 : undefined,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        width: format === 'banner' ? '100%' : undefined,
                        flexDirection: centerButton ? 'column' : undefined,
                      }}
                    >
                      <div
                        style={{
                          position: 'relative',
                          width: format === 'banner' ? '100%' : previewWidth,
                          height:
                            format === 'avatar' ? previewHeight : undefined,
                        }}
                      >
                        <ImagePickerPreview
                          image={image}
                          format={format}
                          altText={altText}
                          previewCrop={previewCrop}
                          previewCropToCircle={previewCropToCircle}
                          defaultImage={defaultImage}
                          previewWidth={previewWidth}
                          previewHeight={previewHeight}
                          insecureSVG={insecureSVG}
                          overlayButton={overlayButton}
                          dragActive={dragActive}
                          qaId={qaId}
                        />

                        {!hideRemove && image && (
                          <TrashDeleteButton
                            title={getText('Remove')}
                            data-qa-id={`${qaId}-remove-image`}
                            onClick={() => {
                              if (showConfirmationModal) {
                                showConfirmationModal();
                              } else {
                                onChange(null);
                              }
                            }}
                            $isBannerWithOverlay={Boolean(isBannerWithOverlay)}
                            $alignToCircle={Boolean(previewCropToCircle)}
                          >
                            <Icon name="trash" size={14} />
                          </TrashDeleteButton>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {message && overlayButton ? (
                  <ImagePickerOverlayButton
                    id={id}
                    image={image}
                    onClick={onClick}
                    message={message}
                  />
                ) : (
                  message && (
                    <ImagePickerButton
                      id={id}
                      buttonOnly={buttonOnly}
                      previewWidth={previewWidth}
                      previewHeight={previewHeight}
                      message={message}
                    />
                  )
                )}
              </DropContainer>

              {error && <FieldError errorMessage={error} />}
            </div>
          </>
        );
      }}
    </FilestackPicker>
  );
}
